import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, message } from "antd";
import { Search } from "lucide-react";
import Request from "../../../helpers/request.service";
import Auth from "../../../helpers/auth.service";
import AppInput from "../../../components/ui/AppInput";
import { getFirstPermittedUrl } from "../../../helpers/helper";
import { authUrl } from "../../../constants/apiBaseUrl";
import { useAuth } from "../../../lib/auth";

/**
 * Store Selection — BRD §1.1.4.
 *
 * Card grid of the user's active assigned stores. Inactive stores arrive
 * here only as historical assignments (the server pre-filters), but the
 * screen still greys non-ACTIVE cards defensively per PRD §37.
 *
 * Selecting Continue calls POST /auth/active-store to validate membership
 * server-side, persists the chosen storeId in localStorage, then routes to
 * the dashboard. Reload-safe because assignedStores + tokens already live
 * in localStorage (set by loginWithTokens).
 */
export default function StoreSelection() {
  const navigate = useNavigate();
  const { user, assignedStores, setActiveStoreId, loadPermissions, logout } = useAuth();
  const [selectedId, setSelectedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");

  // Defensive: a direct visit without an authenticated session must not be
  // usable. Push back to /login.
  useEffect(() => {
    if (!user) {
      navigate("/login", { replace: true });
    }
  }, [user, navigate]);

  const stores = useMemo(
    () => (Array.isArray(assignedStores) ? assignedStores : []),
    [assignedStores],
  );

  // Super Admin can be offered every active store, so the list may be long —
  // filter by store name or code (case-insensitive).
  const filteredStores = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return stores;
    return stores.filter(
      (s) =>
        (s.storeName || "").toLowerCase().includes(q) ||
        (s.storeCode || "").toLowerCase().includes(q),
    );
  }, [stores, search]);

  const handleSelect = (store) => {
    if (store.status && store.status !== "ACTIVE") return;
    setSelectedId(store.storeId);
  };

  const handleContinue = async () => {
    if (selectedId == null) return;
    setSubmitting(true);
    try {
      await Request.postJson(authUrl.active_store, { storeId: selectedId });
      Auth.activeStoreId = selectedId;
      setActiveStoreId(selectedId);
      // Server-driven gating: load real catalog grants now the active store is
      // set (the store-context filter gates /roles/user-actions).
      await loadPermissions();
      navigate(getFirstPermittedUrl(), { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.message || "Could not select that store. Try again.";
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-[920px] bg-milk border border-border rounded-2xl p-8">
        <div className="text-center mb-6">
          <div className="font-display text-3xl text-cocoa">Choose a store</div>
          <div className="text-mahogany italic text-sm mt-1">
            Pick the location you&apos;ll be working in. You can switch later from the topbar.
          </div>
        </div>

        {stores.length === 0 ? (
          <div className="text-center text-mahogany py-12">
            No active stores are currently assigned to your account. Please contact your
            administrator.
          </div>
        ) : (
          <>
            {stores.length > 6 && (
              <div className="mb-5 max-w-[360px] mx-auto">
                <AppInput
                  name="storeSearch"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by store name or code"
                  prefix={<Search size={16} className="text-mahogany/60" />}
                />
              </div>
            )}
            {filteredStores.length === 0 ? (
              <div className="text-center text-mahogany py-12">
                No stores match &ldquo;{search}&rdquo;.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredStores.map((store) => {
                  const inactive = store.status && store.status !== "ACTIVE";
                  const selected = selectedId === store.storeId;
                  return (
                    <button
                      key={store.storeId}
                      type="button"
                      disabled={inactive}
                      onClick={() => handleSelect(store)}
                      className={[
                        "text-left rounded-xl border p-4 transition-all duration-150",
                        "focus:outline-none focus:ring-2 focus:ring-honey",
                        inactive
                          ? "bg-cream/60 border-border text-mahogany/60 cursor-not-allowed"
                          : selected
                            ? "bg-honey/15 border-honey shadow-sm"
                            : "bg-milk border-border hover:border-honey/60",
                      ].join(" ")}
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-display text-lg text-cocoa">{store.storeName}</div>
                        {store.primary && (
                          <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-honey/20 text-honey">
                            Primary
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-mahogany mt-1">{store.storeCode}</div>
                      <div className="text-sm text-cocoa/80 mt-3">
                        {store.city || "—"}
                        {store.state ? `, ${store.state}` : ""}
                      </div>
                      <div className="mt-3">
                        <span
                          className={[
                            "inline-block text-[11px] px-2 py-0.5 rounded-full",
                            inactive
                              ? "bg-mahogany/10 text-mahogany"
                              : "bg-[#4A7C59]/15 text-[#4A7C59]",
                          ].join(" ")}
                        >
                          {inactive ? "Inactive" : "Active"}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}

        <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
          <Button type="link" onClick={handleSignOut} disabled={submitting}>
            Sign out
          </Button>
          <Button
            type="primary"
            onClick={handleContinue}
            loading={submitting}
            disabled={selectedId == null}
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
}
